import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Square, Check } from 'lucide-react';
import { playSound, SOUNDS } from '../App';
import BuildingAnimation from './BuildingAnimation';
import AppPreviewContainer from './AppPreviewContainer';
import confetti from 'canvas-confetti';

// App templates
const desktopApps = [
  { id: 'dashboard', name: 'AI Analytics Dashboard', icon: '📊', description: 'Sanntids-metrikker og AI-drevne innsikter' },
  { id: 'kanban', name: 'Prosjektstyring', icon: '📋', description: 'Kanban-tavler med AI-oppgaveprioritering' },
  { id: 'content', name: 'AI Innholdsgenerator', icon: '✍️', description: 'Generer tekst, bilder og markedsføringsinnhold' },
];

const mobileApps = [
  { id: 'fitness', name: 'Treningsapp', icon: '💪', description: 'Spor treninger med AI-coaching' },
  { id: 'food', name: 'Matleveringsapp', icon: '🍔', description: 'Bestill mat med AI-anbefalinger' },
  { id: 'chat', name: 'AI-Assistent', icon: '🤖', description: 'Personlig AI-assistent med tale' },
];

const colorThemes = [
  { id: 'blue', name: 'Havblå', primary: '#0000FF' },
  { id: 'green', name: 'Skoggrønn', primary: '#008000' },
  { id: 'purple', name: 'Kongelig Lilla', primary: '#800080' },
  { id: 'red', name: 'Klassisk Rød', primary: '#FF0000' },
  { id: 'teal', name: 'Win95 Teal', primary: '#008080' },
  { id: 'gray', name: 'Sølv', primary: '#808080' },
];

// Win95 Button Component
const Win95Button = ({ children, onClick, disabled, primary, style = {} }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      fontFamily: "'Segoe UI', Tahoma, sans-serif",
      fontSize: '12px',
      padding: '6px 20px',
      minWidth: '80px',
      background: disabled ? '#c0c0c0' : (primary ? 'linear-gradient(180deg, #d0d0d0 0%, #c0c0c0 100%)' : '#c0c0c0'),
      color: disabled ? '#808080' : '#000000',
      border: 'none',
      boxShadow: disabled 
        ? 'inset 1px 1px 0 #808080' 
        : 'inset -1px -1px 0 #404040, inset 1px 1px 0 #ffffff, inset -2px -2px 0 #808080, inset 2px 2px 0 #dfdfdf',
      cursor: disabled ? 'default' : 'pointer',
      ...style
    }}
  >
    {children}
  </button>
);

// Win95 Radio Button
const Win95Radio = ({ checked, onChange, label, name }) => (
  <label style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: '8px', 
    cursor: 'pointer',
    fontFamily: "'Segoe UI', Tahoma, sans-serif",
    fontSize: '12px',
    padding: '4px 0'
  }}>
    <div style={{
      width: '13px',
      height: '13px',
      borderRadius: '50%',
      background: '#ffffff',
      border: '1px solid #808080',
      boxShadow: 'inset 1px 1px 0 #404040, inset -1px -1px 0 #ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {checked && (
        <div style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          background: '#000000'
        }} />
      )}
    </div>
    <input 
      type="radio" 
      checked={checked} 
      onChange={onChange}
      name={name}
      style={{ display: 'none' }}
    />
    <span>{label}</span>
  </label>
);

// Win95 Checkbox
const Win95Checkbox = ({ checked, onChange, label }) => (
  <label style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: '8px', 
    cursor: 'pointer',
    fontFamily: "'Segoe UI', Tahoma, sans-serif",
    fontSize: '12px',
    padding: '4px 0'
  }}>
    <div style={{
      width: '13px',
      height: '13px',
      background: '#ffffff',
      border: '1px solid #808080',
      boxShadow: 'inset 1px 1px 0 #404040, inset -1px -1px 0 #ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {checked && (
        <Check size={11} color="#000000" strokeWidth={3} />
      )}
    </div>
    <input 
      type="checkbox" 
      checked={checked} 
      onChange={onChange}
      style={{ display: 'none' }}
    />
    <span>{label}</span>
  </label>
);

// Win95 Group Box
const Win95GroupBox = ({ title, children }) => (
  <div style={{
    border: '1px solid #808080',
    borderRadius: '0',
    padding: '16px 12px 12px',
    margin: '8px 0',
    position: 'relative',
    background: '#c0c0c0'
  }}>
    <span style={{
      position: 'absolute',
      top: '-8px',
      left: '10px',
      background: '#c0c0c0',
      padding: '0 6px',
      fontFamily: "'Segoe UI', Tahoma, sans-serif",
      fontSize: '12px',
      fontWeight: '600'
    }}>
      {title}
    </span>
    {children}
  </div>
);

// Win95 Progress Bar
const Win95ProgressBar = ({ progress }) => (
  <div style={{
    width: '100%',
    height: '20px',
    background: '#c0c0c0',
    border: '2px solid',
    borderColor: '#808080 #ffffff #ffffff #808080',
    padding: '2px'
  }}>
    <div style={{
      width: `${progress}%`,
      height: '100%',
      background: '#000080',
      transition: 'width 0.3s ease'
    }} />
  </div>
);

// Step indicator with Win95 style
const Win95StepIndicator = ({ currentStep }) => {
  const steps = ['1. Plattform', '2. Applikasjon', '3. Tema', '4. Bygg'];
  
  return (
    <div style={{
      display: 'flex',
      borderBottom: '1px solid #808080',
      marginBottom: '16px',
      paddingBottom: '8px'
    }}>
      {steps.map((step, i) => (
        <div 
          key={i}
          style={{
            flex: 1,
            textAlign: 'center',
            fontFamily: "'Segoe UI', Tahoma, sans-serif",
            fontSize: '11px',
            color: i === currentStep ? '#000080' : (i < currentStep ? '#008000' : '#808080'),
            fontWeight: i === currentStep ? 'bold' : 'normal',
            padding: '4px',
            background: i === currentStep ? '#e0e0e0' : 'transparent',
            borderBottom: i === currentStep ? '2px solid #000080' : 'none'
          }}
        >
          {i < currentStep && '✓ '}{step}
        </div>
      ))}
    </div>
  );
};

export default function AppWizard({ 
  zIndex, 
  onClose, 
  onMinimize, 
  onFocus,
  position = { x: 200, y: 60 },
  onPositionChange,
  onOpenContact 
}) {
  const [step, setStep] = useState(0);
  const [platform, setPlatform] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(colorThemes[0]);
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildProgress, setBuildProgress] = useState(0);
  const [buildComplete, setBuildComplete] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [features, setFeatures] = useState({
    darkMode: true,
    analytics: true,
    notifications: false,
    auth: true
  });
  
  const windowRef = useRef(null);

  const handleClose = (e) => {
    e?.stopPropagation();
    playSound(SOUNDS.windowClose);
    onClose();
  };

  const handleMinimize = (e) => {
    e?.stopPropagation();
    playSound(SOUNDS.click);
    onMinimize();
  };

  const handleMaximize = (e) => {
    e?.stopPropagation();
    playSound(SOUNDS.click);
    setIsMaximized(!isMaximized);
  };

  const nextStep = () => {
    playSound(SOUNDS.click);
    if (step < 3) {
      setStep(step + 1);
    } else {
      startBuild();
    }
  };

  const prevStep = () => {
    playSound(SOUNDS.click);
    if (step > 0) setStep(step - 1);
  };

  const startBuild = () => {
    setIsBuilding(true);
    setBuildProgress(0);
    
    const interval = setInterval(() => {
      setBuildProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setBuildComplete(true);
            confetti({
              particleCount: 100,
              spread: 70,
              origin: { y: 0.6 }
            });
          }, 300);
          return 100;
        }
        return prev + Math.random() * 8 + 2;
      });
    }, 100);
  };

  const canProceed = () => {
    switch (step) {
      case 0: return platform !== null;
      case 1: return selectedApp !== null;
      case 2: return selectedTheme !== null;
      case 3: return true;
      default: return false;
    }
  };

  const apps = platform === 'desktop' ? desktopApps : mobileApps;

  // Restart wizard
  const restartWizard = () => {
    playSound(SOUNDS.click);
    setStep(0);
    setPlatform(null);
    setSelectedApp(null);
    setSelectedTheme(colorThemes[0]);
    setIsBuilding(false);
    setBuildProgress(0);
    setBuildComplete(false);
    setCompanyName('');
    setFeatures({ darkMode: true, analytics: true, notifications: false, auth: true });
  };

  const windowStyle = isMaximized
    ? { zIndex, left: 0, top: 0, width: '100%', height: 'calc(100vh - 36px)' }
    : { zIndex, left: position.x, top: position.y, width: '560px' };

  // Building screen
  if (isBuilding && !buildComplete) {
    return (
      <motion.div
        ref={windowRef}
        className="win-window floating-window"
        style={windowStyle}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={onFocus}
      >
        {/* Title bar */}
        <div className="win-window-header">
          <div className="win-window-title">
            <span>🔨 Bygger Applikasjon...</span>
          </div>
          <div className="win-window-controls">
            <button className="win-ctrl-btn" onClick={handleMinimize}><Minus size={10} /></button>
            <button className="win-ctrl-btn" onClick={handleMaximize}><Square size={8} /></button>
            <button className="win-ctrl-btn win-close" onClick={handleClose}><X size={10} /></button>
          </div>
        </div>
        
        <div style={{ 
          background: '#c0c0c0', 
          padding: '24px',
          height: 'calc(100% - 28px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏗️</div>
            <div style={{ 
              fontFamily: "'Segoe UI', Tahoma, sans-serif",
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              Bygger din {selectedApp?.name}...
            </div>
            <div style={{ 
              fontFamily: "'Segoe UI', Tahoma, sans-serif",
              fontSize: '11px',
              color: '#666'
            }}>
              Vennligst vent mens vi genererer applikasjonen din
            </div>
          </div>
          
          <div style={{ width: '80%', maxWidth: '400px' }}>
            <Win95ProgressBar progress={buildProgress} />
            <div style={{ 
              textAlign: 'center', 
              marginTop: '8px',
              fontFamily: "'Segoe UI', Tahoma, sans-serif",
              fontSize: '11px'
            }}>
              {Math.round(buildProgress)}% Fullført
            </div>
          </div>
          
          <div style={{ 
            marginTop: '24px',
            fontFamily: "'Segoe UI', Tahoma, sans-serif",
            fontSize: '10px',
            color: '#666'
          }}>
            {buildProgress < 25 && 'Initialiserer prosjektstruktur...'}
            {buildProgress >= 25 && buildProgress < 50 && 'Installerer avhengigheter...'}
            {buildProgress >= 50 && buildProgress < 75 && 'Genererer komponenter...'}
            {buildProgress >= 75 && buildProgress < 100 && 'Fullfører bygging...'}
          </div>
        </div>
      </motion.div>
    );
  }

  // Build complete screen - show modern preview
  if (buildComplete) {
    // Build customization object for preview
    const customization = {
      theme: {
        primary: selectedTheme?.primary || '#6366f1',
        secondary: selectedTheme?.primary || '#8b5cf6',
        gradient: `linear-gradient(135deg, ${selectedTheme?.primary || '#6366f1'} 0%, ${selectedTheme?.primary || '#8b5cf6'} 100%)`
      },
      companyName: companyName || 'Ditt Selskap',
      features
    };

    return (
      <motion.div
        ref={windowRef}
        className="win-window floating-window"
        style={{ 
          zIndex, 
          left: isMaximized ? 0 : position.x, 
          top: isMaximized ? 0 : position.y, 
          width: isMaximized ? '100%' : '900px',
          height: isMaximized ? 'calc(100vh - 36px)' : '650px'
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={onFocus}
        drag={!isMaximized}
        dragMomentum={false}
        dragConstraints={{ left: -200, right: 800, top: 0, bottom: 400 }}
        onDragEnd={(event, info) => {
          if (!isMaximized) {
            onPositionChange?.({ 
              x: Math.max(0, position.x + info.offset.x), 
              y: Math.max(0, position.y + info.offset.y) 
            });
          }
        }}
      >
        <div className="win-window-header" style={{ cursor: isMaximized ? 'default' : 'grab' }}>
          <div className="win-window-title">
            <span>✅ {selectedApp?.name} - Forhåndsvisning</span>
          </div>
          <div className="win-window-controls">
            <button className="win-ctrl-btn" onClick={handleMinimize}><Minus size={10} /></button>
            <button className="win-ctrl-btn" onClick={handleMaximize}><Square size={8} /></button>
            <button className="win-ctrl-btn win-close" onClick={handleClose}><X size={10} /></button>
          </div>
        </div>
        
        <div style={{ 
          height: 'calc(100% - 28px)',
          overflow: 'hidden',
          background: '#0a0a1a'
        }}>
          <AppPreviewContainer
            app={selectedApp}
            platform={platform}
            customization={customization}
            onBack={restartWizard}
            onRestart={restartWizard}
            onContact={() => {
              onOpenContact?.();
              handleClose();
            }}
          />
        </div>
      </motion.div>
    );
  }

  // Main wizard
  return (
    <motion.div
      ref={windowRef}
      className="win-window floating-window"
      style={windowStyle}
      initial={{ opacity: 0, scale: 0.92, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={onFocus}
      drag={!isMaximized}
      dragMomentum={false}
      dragElastic={0.05}
      dragConstraints={{ left: -200, right: 1200, top: 0, bottom: 600 }}
      onDragEnd={(event, info) => {
        if (!isMaximized) {
          onPositionChange?.({ 
            x: Math.max(0, position.x + info.offset.x), 
            y: Math.max(0, position.y + info.offset.y) 
          });
        }
      }}
    >
      {/* Title bar */}
      <div className="win-window-header" style={{ cursor: isMaximized ? 'default' : 'grab' }}>
        <div className="win-window-title">
          <span>🧙 App-veiviser</span>
        </div>
        <div className="win-window-controls">
          <button className="win-ctrl-btn" onClick={handleMinimize}><Minus size={10} /></button>
          <button className="win-ctrl-btn" onClick={handleMaximize}><Square size={8} /></button>
          <button className="win-ctrl-btn win-close" onClick={handleClose}><X size={10} /></button>
        </div>
      </div>
      
      {/* Content */}
      <div style={{ 
        background: '#c0c0c0', 
        padding: '16px',
        height: 'calc(100% - 28px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Step indicator */}
        <Win95StepIndicator currentStep={step} />
        
        {/* Wizard content area */}
        <div style={{ 
          flex: 1, 
          background: '#ffffff',
          border: '2px solid',
          borderColor: '#808080 #ffffff #ffffff #808080',
          padding: '16px',
          overflow: 'auto'
        }}>
          <AnimatePresence mode="wait">
            {/* Step 0: Platform Selection */}
            {step === 0 && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <div style={{ 
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '8px'
                }}>
                  Velkommen til App-veiviseren!
                </div>
                <div style={{ 
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '12px',
                  marginBottom: '20px',
                  color: '#444'
                }}>
                  Denne veiviseren hjelper deg med å lage din egne applikasjon. Velg en plattform for å begynne.
                </div>
                
                <Win95GroupBox title="Velg Plattform">
                  <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                    {/* Desktop option */}
                    <div 
                      onClick={() => setPlatform('desktop')}
                      style={{
                        flex: 1,
                        padding: '20px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: platform === 'desktop' ? '#000080' : '#e0e0e0',
                        color: platform === 'desktop' ? '#ffffff' : '#000000',
                        border: '2px solid',
                        borderColor: platform === 'desktop' 
                          ? '#000040 #4040ff #4040ff #000040'
                          : '#ffffff #808080 #808080 #ffffff'
                      }}
                    >
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖥️</div>
                      <div style={{ fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: '12px', fontWeight: 'bold' }}>
                        Skrivebord
                      </div>
                      <div style={{ fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: '10px', marginTop: '4px', opacity: 0.8 }}>
                        Nettbasert dashboard
                      </div>
                    </div>
                    
                    {/* Mobile option */}
                    <div 
                      onClick={() => setPlatform('mobile')}
                      style={{
                        flex: 1,
                        padding: '20px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: platform === 'mobile' ? '#000080' : '#e0e0e0',
                        color: platform === 'mobile' ? '#ffffff' : '#000000',
                        border: '2px solid',
                        borderColor: platform === 'mobile' 
                          ? '#000040 #4040ff #4040ff #000040'
                          : '#ffffff #808080 #808080 #ffffff'
                      }}
                    >
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>📱</div>
                      <div style={{ fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: '12px', fontWeight: 'bold' }}>
                        Mobil
                      </div>
                      <div style={{ fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: '10px', marginTop: '4px', opacity: 0.8 }}>
                        iOS & Android app
                      </div>
                    </div>
                  </div>
                </Win95GroupBox>
              </motion.div>
            )}
            
            {/* Step 1: App Selection */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <div style={{ 
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '8px'
                }}>
                  Velg Applikasjonstype
                </div>
                <div style={{ 
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '12px',
                  marginBottom: '16px',
                  color: '#444'
                }}>
                  Velg typen {platform === 'desktop' ? 'skrivebords' : 'mobil'}applikasjon du vil bygge.
                </div>
                
                <Win95GroupBox title="Tilgjengelige Maler">
                  {apps.map((app, i) => (
                    <div 
                      key={app.id}
                      onClick={() => setSelectedApp(app)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px',
                        cursor: 'pointer',
                        background: selectedApp?.id === app.id ? '#000080' : (i % 2 === 0 ? '#f0f0f0' : '#e8e8e8'),
                        color: selectedApp?.id === app.id ? '#ffffff' : '#000000',
                        marginBottom: '2px'
                      }}
                    >
                      <div style={{ fontSize: '24px' }}>{app.icon}</div>
                      <div>
                        <div style={{ fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: '12px', fontWeight: 'bold' }}>
                          {app.name}
                        </div>
                        <div style={{ fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: '10px', opacity: 0.8 }}>
                          {app.description}
                        </div>
                      </div>
                      {selectedApp?.id === app.id && (
                        <div style={{ marginLeft: 'auto' }}>✓</div>
                      )}
                    </div>
                  ))}
                </Win95GroupBox>
              </motion.div>
            )}
            
            {/* Step 2: Customization */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <div style={{ 
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '8px'
                }}>
                  Tilpass Applikasjonen
                </div>
                <div style={{ 
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '12px',
                  marginBottom: '16px',
                  color: '#444'
                }}>
                  Personaliser din {selectedApp?.name} med disse valgene.
                </div>
                
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <Win95GroupBox title="Selskapsnavn">
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Skriv inn selskapsnavn..."
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          fontFamily: "'Segoe UI', Tahoma, sans-serif",
                          fontSize: '12px',
                          border: '2px solid',
                          borderColor: '#808080 #ffffff #ffffff #808080',
                          background: '#ffffff',
                          boxSizing: 'border-box'
                        }}
                      />
                    </Win95GroupBox>
                    
                    <Win95GroupBox title="Fargetema">
                      {colorThemes.map((theme) => (
                        <Win95Radio
                          key={theme.id}
                          checked={selectedTheme?.id === theme.id}
                          onChange={() => setSelectedTheme(theme)}
                          label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ 
                                width: '14px', 
                                height: '14px', 
                                background: theme.primary, 
                                border: '1px solid #808080'
                              }} />
                              {theme.name}
                            </span>
                          }
                          name="theme"
                        />
                      ))}
                    </Win95GroupBox>
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <Win95GroupBox title="Funksjoner">
                      <Win95Checkbox
                        checked={features.darkMode}
                        onChange={() => setFeatures({...features, darkMode: !features.darkMode})}
                        label="Mørk Modus"
                      />
                      <Win95Checkbox
                        checked={features.analytics}
                        onChange={() => setFeatures({...features, analytics: !features.analytics})}
                        label="Analyse-Dashboard"
                      />
                      <Win95Checkbox
                        checked={features.notifications}
                        onChange={() => setFeatures({...features, notifications: !features.notifications})}
                        label="Push-Varsler"
                      />
                      <Win95Checkbox
                        checked={features.auth}
                        onChange={() => setFeatures({...features, auth: !features.auth})}
                        label="Brukerautentisering"
                      />
                    </Win95GroupBox>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Step 3: Review */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <div style={{ 
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '8px'
                }}>
                  Gjennomgå & Bygg
                </div>
                <div style={{ 
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '12px',
                  marginBottom: '16px',
                  color: '#444'
                }}>
                  Vennligst gjennomgå valgene dine før bygging.
                </div>
                
                <Win95GroupBox title="Byggeoppsummering">
                  <table style={{ 
                    width: '100%', 
                    fontFamily: "'Segoe UI', Tahoma, sans-serif",
                    fontSize: '12px',
                    borderCollapse: 'collapse'
                  }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '6px 0', fontWeight: 'bold', width: '120px' }}>Plattform:</td>
                        <td>{platform === 'desktop' ? '🖥️ Skrivebord' : '📱 Mobil'}</td>
                      </tr>
                      <tr style={{ background: '#f0f0f0' }}>
                        <td style={{ padding: '6px 0', fontWeight: 'bold' }}>Applikasjon:</td>
                        <td>{selectedApp?.icon} {selectedApp?.name}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px 0', fontWeight: 'bold' }}>Tema:</td>
                        <td style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ 
                            width: '14px', 
                            height: '14px', 
                            background: selectedTheme?.primary, 
                            border: '1px solid #808080',
                            display: 'inline-block'
                          }} />
                          {selectedTheme?.name}
                        </td>
                      </tr>
                      {companyName && (
                        <tr style={{ background: '#f0f0f0' }}>
                          <td style={{ padding: '6px 0', fontWeight: 'bold' }}>Selskap:</td>
                          <td>{companyName}</td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ padding: '6px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Funksjoner:</td>
                        <td>
                          {features.darkMode && '✓ Mørk Modus '} 
                          {features.analytics && '✓ Analyse '}
                          {features.notifications && '✓ Varsler '}
                          {features.auth && '✓ Autentisering'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Win95GroupBox>
                
                <div style={{
                  background: '#ffffcc',
                  border: '1px solid #808080',
                  padding: '12px',
                  marginTop: '12px',
                  fontFamily: "'Segoe UI', Tahoma, sans-serif",
                  fontSize: '11px'
                }}>
                  <strong>💡 Tips:</strong> Klikk "Bygg" for å generere en forhåndsvisning av applikasjonen din. 
                  For en produksjonsklar versjon, kontakt teamet vårt!
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Navigation buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #808080'
        }}>
          <Win95Button onClick={handleClose}>
            Avbryt
          </Win95Button>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <Win95Button onClick={prevStep} disabled={step === 0}>
              &lt; Tilbake
            </Win95Button>
            <Win95Button onClick={nextStep} disabled={!canProceed()} primary>
              {step === 3 ? 'Bygg >' : 'Neste >'}
            </Win95Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
